import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Box,
  ListItemIcon,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Business,
  ReceiptLong,
  ExitToApp,
  ChevronRight,
  ChevronLeft,
  AccountBalanceWallet,
  Group,
  LocationOn,
  AssignmentInd,
  MonetizationOn,
} from "@mui/icons-material";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import NotesIcon from '@mui/icons-material/Notes';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import BackupIcon from '@mui/icons-material/Backup';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getLoggedInUserEmail, getLoggedInUserRole } from "../utils/auth";
import CategoryIcon from '@mui/icons-material/Category';

const drawerWidth = 240;
const collapsedWidth = 72;

const menuItems = [
  {
    text: "Analytics",
    icon: <AutoGraphIcon />,
    path: "/",
  },
  {
    text: "Customer Ledger",
    icon: <AssignmentInd />,
    path: "/customer-ledger",
  },
  {
    text: "Manage Firms",
    icon: <Business />,
    path: "/firms",
  },
  {
    text: "Manage Invoices",
    icon: <ReceiptLong />,
    path: "/invoices",
  },
  {
    text: "Suppliers Ledger",
    icon: <AccountBalanceWallet />,
    path: "/suppliers-ledger",
  },
  {
    text: "Admin Users",
    icon: <Group />,
    path: "/admin",
  },
  {
    text: "Service Locations",
    icon: <LocationOn />,
    path: "/service-location",
  },
  {
    text: "Credit Notes",
    icon: <MonetizationOn />,
    path: "/credit-notes",
  },
  {
    text: "Reporting",
    icon: <NotesIcon />,
    path: "/reporting",
  },
  {
    text: "Upload",
    icon: <FileUploadIcon />,
    path: "/upload",
  },
  {
    text: "Products",
    icon: <CategoryIcon />,
    path: "/products",
  },
  {
    text: "Notifications",
    icon: <WhatsAppIcon />,
    path: "/whatsapp-notifications",
  },
  {
    text: "Database Backup",
    icon: <BackupIcon />,
    path: "/database-backup",
    roles: ["admin"],
  },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();

  const toggleDrawer = () => {
    setCollapsed(!collapsed);
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    navigate("/logout");
  };

  const email = getLoggedInUserEmail();
  const role = getLoggedInUserRole();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        transition: "width 0.3s",
        "& .MuiDrawer-paper": {
          width: collapsed ? collapsedWidth : drawerWidth,
          transition: "width 0.3s",
          overflowX: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: 2,
          py: 1,
        }}
      >
        {!collapsed && <Typography variant="h6">Meri Dukan</Typography>}
        <IconButton onClick={toggleDrawer}>
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      {!collapsed && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
          {email ? `${email}` : "Not logged in"}
        </Typography>
      )}
      <Divider />
      <List>
        {menuItems
          .filter(({ text }) => {
            if (role === "auditor") {
              return text === "Manage Invoices";
            }
            return true;
          })
          .filter(({ roles }) => {
            if (!roles || roles.length === 0) {
              return true;
            }

            if (!role) {
              return false;
            }

            return roles.includes(role);
          })
          .map(({ text, icon, path }) => (
            <Tooltip title={collapsed ? text : ""} placement="right" key={text}>
              <ListItemButton
                component={Link}
                to={path}
                selected={isActive(path)}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  color: isActive(path)
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  backgroundColor: isActive(path)
                    ? theme.palette.action.selected
                    : "transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(path)
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    minWidth: 0,
                    mr: collapsed ? "auto" : 2,
                    justifyContent: "center",
                  }}
                >
                  {icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={text} />}
              </ListItemButton>
            </Tooltip>
          ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Tooltip title="Logout" placement="right">
          <IconButton
            onClick={handleLogout}
            sx={{
              color: theme.palette.primary.main,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ExitToApp />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          textAlign: "center",
          px: 2,
          py: 2,
          fontSize: 13,
          color: "text.secondary",
          whiteSpace: "nowrap",
        }}
      >
        {!collapsed ? (
          <Typography variant="caption" color="text.secondary">
            Built with{" "}
            <span role="img" aria-label="heart">
              ❤️
            </span>{" "}
            in India
          </Typography>
        ) : (
          <Tooltip title="Built with love in India" placement="right">
            <Box component="span" role="img" aria-label="heart">
              ❤️
            </Box>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
